/*
 * GenericStore is a generic, extensible, persistent, and reactive store system for Supabase data caching.
 *
 * IMPORTANT: All business logic (such as linking users, companies, and games, or filtering/transforming data)
 * should be implemented outside of GenericStore, in application-level utilities or adapters.
 *
 * GenericStore is only responsible for:
 *   - Caching data
 *   - Syncing data with remote sources
 *   - Adapting between remote and local data formats (via adapters)
 *   - Notifying subscribers of changes
 *
 * This separation ensures that business logic is decoupled from storage logic, making the system more maintainable.
 */

/*
GenericStore & StoreRegistry Usage Example
=========================================

Suppose your Supabase `users` table returns this shape:

  // Remote type (from Supabase)
  type SupabaseUser = {
    id: string;
    username: string;
    created_at: string;
  };

But in your app, you want to work with this local type:

  // Local type (in your app)
  type User = {
    id: string;
    name: string;
    joined: Date;
  };

Adapter for transformation:

  const userAdapter = {
    fromRemote: (data: SupabaseUser[]): User[] =>
      data.map(u => ({
        id: u.id,
        name: u.username,
        joined: new Date(u.created_at),
      })),
    toRemote: (data: User[]): SupabaseUser[] =>
      data.map(u => ({
        id: u.id,
        username: u.name,
        created_at: u.joined.toISOString(),
      })),
  };

Create and use the store:

  import { storeRegistry } from './GenericStore';
  import { DataManager } from './DataManager';

  // 1. Register the store with types and adapter
  const usersStore = storeRegistry.getStore<User, SupabaseUser>({
    key: 'users',
    ttlMs: 1000 * 60 * 10, // 10 minutes
    adapter: userAdapter,
  });

  // 2. Load from Supabase and cache locally
  async function loadUsers() {
    const supabase = DataManager.getInstance().getSupabase();
    const { data } = await supabase!.from('users').select('*');
    await usersStore.syncFromRemote(async () => data || []);
  }

  // 3. Subscribe to changes (reactive)
  usersStore.subscribe(users => {
    console.log('Users in app format:', users);
  });

  // 4. Access cached data synchronously
  const cachedUsers = usersStore.getData();

  // 5. Push local changes to Supabase
  async function saveUsersToSupabase() {
    await usersStore.syncToRemote(async (remoteUsers) => {
      // Example: upsert all users
      const supabase = DataManager.getInstance().getSupabase();
      await supabase!.from('users').upsert(remoteUsers);
    });
  }

=========================================
*/ 


// GenericStore.ts
// A generic, extensible, persistent, and reactive store system for Supabase data caching.
// Designed for easy packaging as an npm module.

import { createStore, get, set, del } from 'idb-keyval';

type Listener<T> = (data: T[]) => void;

export interface StoreOptions<T, Remote = T> {
  key: string; // Unique key for the store (table/bucket name)
  ttlMs?: number; // Optional: time-to-live for cache (ms)
  adapter?: {
    fromRemote?: (data: Remote[]) => T[];
    toRemote?: (data: T[]) => Remote[];
  };
}

interface CachedData<T> {
  data: T[];
  timestamp: number;
}

export class GenericStore<T, Remote = T> {
  private data: T[] = [];
  private listeners: Listener<T>[] = [];
  private store: ReturnType<typeof createStore>;
  private key: string;
  private ttlMs?: number;
  private adapter?: StoreOptions<T, Remote>['adapter'];
  private initialized = false;

  constructor(options: StoreOptions<T, Remote>) {
    this.key = options.key;
    this.ttlMs = options.ttlMs;
    this.adapter = options.adapter;
    this.store = createStore('generic-store-db-v2', this.key);
    // Don't load from cache immediately - wait for explicit initialization
  }

  private async initialize() {
    if (this.initialized) return;
    
    // Wait for global IndexedDB initialization
    await initializeIndexedDB();
    
    try {
      await this.loadFromCache();
      this.initialized = true;
    } catch (error) {
      console.warn(`Failed to initialize store for key "${this.key}":`, error);
      // Mark as initialized even if it fails to prevent infinite retries
      this.initialized = true;
    }
  }

  private async loadFromCache() {
    // Check if IndexedDB is supported
    if (!isIndexedDBSupported()) {
      console.warn(`IndexedDB not supported, skipping cache load for key "${this.key}"`);
      return;
    }

    try {
      const cached: CachedData<T> | undefined = await get(this.key, this.store);
      if (cached && (!this.ttlMs || Date.now() - cached.timestamp < this.ttlMs)) {
        this.data = cached.data;
        this.notify();
      } else if (cached) {
        // Expired
        await del(this.key, this.store);
      }
    } catch (error) {
      // Silently handle IndexedDB initialization errors - this is normal on first load
      if (error instanceof Error && (
        error.message.includes('object stores was not found') ||
        error.message.includes('NotFoundError') ||
        error.name === 'NotFoundError'
      )) {
        // This is expected on first load - just continue with empty data
        // Don't log this as it creates unnecessary noise
        this.data = [];
        this.notify();
        return;
      }
      
      // Log other errors as warnings
      console.warn(`Failed to load cache for key "${this.key}":`, error);
      
      // For other errors, try to recreate the store once
      try {
        await this.recreateStore();
        // Try loading again once
        const cached: CachedData<T> | undefined = await get(this.key, this.store);
        if (cached && (!this.ttlMs || Date.now() - cached.timestamp < this.ttlMs)) {
          this.data = cached.data;
          this.notify();
          return;
        }
      } catch (recreateError) {
        console.warn(`Failed to recreate store for key "${this.key}":`, recreateError);
      }
      
      // If there's still an error, just continue with empty data
      // Don't clear the data if it's already set (might be from remote sync)
      if (this.data.length === 0) {
        this.data = [];
        this.notify();
      }
    }
  }

  private async saveToCache() {
    // Check if IndexedDB is supported
    if (!isIndexedDBSupported()) {
      console.warn(`IndexedDB not supported, skipping cache save for key "${this.key}"`);
      return;
    }

    try {
      const cached: CachedData<T> = { data: this.data, timestamp: Date.now() };
      await set(this.key, cached, this.store);
    } catch (error) {
      // Silently handle IndexedDB initialization errors - this is normal on first save
      if (error instanceof Error && (
        error.message.includes('object stores was not found') ||
        error.message.includes('NotFoundError') ||
        error.name === 'NotFoundError'
      )) {
        // This is expected on first save - silently continue
        // The cache will be created on the next operation
        return;
      }
      
      // Log other errors as warnings
      console.warn(`Failed to save cache for key "${this.key}":`, error);
      
      // For other errors, try to recreate the store once
      try {
        // Delete the current store and recreate it
        await this.recreateStore();
        // Try saving again once
        const cached: CachedData<T> = { data: this.data, timestamp: Date.now() };
        await set(this.key, cached, this.store);
      } catch (recreateError) {
        console.warn(`Failed to recreate store for key "${this.key}":`, recreateError);
      }
      // Continue without caching if there's still an error
    }
  }

  private async recreateStore() {
    try {
      // Delete the current database to force recreation
      const dbName = 'generic-store-db-v2';
      const request = indexedDB.deleteDatabase(dbName);
      
      await new Promise<void>((resolve, reject) => {
        request.onsuccess = () => {
          resolve();
        };
        
        request.onerror = () => {
          console.error(`Error deleting database ${dbName}`);
          reject(new Error('Failed to delete database'));
        };
      });

      // Wait a bit for the deletion to complete
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Recreate the store
      this.store = createStore('generic-store-db-v2', this.key);
      
      // Wait a bit more for the store to be ready
      await new Promise(resolve => setTimeout(resolve, 100));
      
    } catch (error) {
      console.warn('Failed to recreate store:', error);
      throw error;
    }
  }

  setData(data: T[]) {
    
    // Check if the data has actually changed meaningfully
    const currentData = this.data;
    const hasChanges = this.hasMeaningfulChanges(currentData, data);
    
    if (hasChanges) {
      this.data = data;
      this.saveToCache();
      this.notify();
    } else {
      // Still update the data but don't notify
      this.data = data;
      this.saveToCache();
    }
  }

  /**
   * Check if there are meaningful changes between old and new data
   * This prevents unnecessary notifications when data is essentially the same
   */
  private hasMeaningfulChanges(oldData: T[], newData: T[]): boolean {
    // If lengths are different, there's definitely a change
    if (oldData.length !== newData.length) {
      return true;
    }
    
    // If both arrays are empty, no change
    if (oldData.length === 0 && newData.length === 0) {
      return false;
    }
    
    // Check if any items have changed by comparing their IDs and key properties
    // This is more efficient than deep comparison for large datasets
    const oldMap = new Map(oldData.map(item => [this.getItemId(item), this.getItemHash(item)]));
    const newMap = new Map(newData.map(item => [this.getItemId(item), this.getItemHash(item)]));
    
    // Check if any keys are different
    if (oldMap.size !== newMap.size) {
      return true;
    }
    
    for (const [key, oldHash] of oldMap) {
      const newHash = newMap.get(key);
      if (newHash !== oldHash) {
        return true;
      }
    }
    
    return false;
  }
  
  /**
   * Get a unique identifier for an item (assumes items have an 'id' property)
   */
  private getItemId(item: T): string {
    return (item as any).id || JSON.stringify(item);
  }
  
  /**
   * Get a hash of the item's key properties for change detection
   * This is more efficient than deep comparison
   */
  private getItemHash(item: T): string {
    // For strings items, focus on the most important fields including translations
    if ((item as any).source_text !== undefined) {
      const translationsHash = (item as any).translations ? 
        JSON.stringify((item as any).translations.map((t: any) => ({ id: t.id, lang: t.language_code, text: t.translated_text }))) : 
        '';
      return `${(item as any).source_text}|${(item as any).abbrev}|${(item as any).status}|${translationsHash}`;
    }
    
    // For document items, focus on the most important fields including data content
    if ((item as any).data !== undefined && Array.isArray((item as any).data) && (item as any).name !== undefined) {
      const name = (item as any).name || '';
      const tags = Array.isArray((item as any).tags) ? (item as any).tags.join(',') : '';
      const settings = (item as any).settings || {};
      const updatedAt = (item as any).updated_at || '';
      // Include a hash of the data content to detect document changes
      const dataHash = JSON.stringify((item as any).data);
      
      return `${name}|${tags}|${JSON.stringify(settings)}|${updatedAt}|${dataHash}`;
    }
    
    // For blueprint items (templates), include data.sections for role visibility changes
    if ((item as any).type !== undefined && ((item as any).type === 'document' || (item as any).type === 'glossary' || (item as any).type === 'Library Object')) {
      const name = (item as any).name || '';
      const description = (item as any).description || '';
      const tags = Array.isArray((item as any).tags) ? (item as any).tags.join(',') : '';
      const version = (item as any).version?.version || 0;
      const settings = (item as any).settings || {};
      // Include data.sections to detect role visibility changes
      const dataHash = (item as any).data ? JSON.stringify((item as any).data) : '';
      
      const hash = `${name}|${description}|${tags}|${version}|${JSON.stringify(settings)}|${dataHash}`;
      return hash;
    }
    
    // For dialog items, include data (sections/nodes) and updated_at to detect content changes
    if ((item as any).dialog_key !== undefined && (item as any).data !== undefined && !Array.isArray((item as any).data)) {
      const name = (item as any).name || '';
      const dialogKey = (item as any).dialog_key || '';
      const description = (item as any).description || '';
      const tags = Array.isArray((item as any).tags) ? (item as any).tags.join(',') : '';
      const status = (item as any).status || '';
      const updatedAt = (item as any).updated_at || '';
      // Include data hash to detect section/node/field changes
      const dataHash = (item as any).data ? JSON.stringify((item as any).data) : '';
      
      return `${name}|${dialogKey}|${description}|${tags}|${status}|${updatedAt}|${dataHash}`;
    }
    
    // For other types, use a simple hash of key properties
    // Include updated_at when available as a reliable change indicator
    const keyProps = Object.keys(item).slice(0, 5); // Limit to first 5 properties
    let hash = keyProps.map(key => (item as any)[key]).join('|');
    if ((item as any).updated_at !== undefined) {
      hash += `|${(item as any).updated_at}`;
    }
    return hash;
  }

  /**
   * Update a single item in the store without triggering a full data replacement
   * This is more efficient for single item updates
   */
  updateItem(itemId: string, updater: (item: T) => T): boolean {
    const currentData = this.data;
    const itemIndex = currentData.findIndex(item => this.getItemId(item) === itemId);
    
    if (itemIndex === -1) {
      return false;
    }
    
    const oldItem = currentData[itemIndex];
    const newItem = updater(oldItem);
    
    // Check if the item actually changed
    if (this.getItemHash(oldItem) === this.getItemHash(newItem)) {
      return false;
    }
    
    // Create new array with updated item
    const updatedData = [...currentData];
    updatedData[itemIndex] = newItem;
    
    // Update data and notify
    this.data = updatedData;
    this.saveToCache();
    this.notify();
    
    return true;
  }

  /**
   * Update multiple items in the store efficiently
   */
  updateItems(updaters: Array<{ itemId: string; updater: (item: T) => T }>): number {
    let updatedCount = 0;
    const currentData = [...this.data];
    let hasChanges = false;
    
    for (const { itemId, updater } of updaters) {
      const itemIndex = currentData.findIndex(item => this.getItemId(item) === itemId);
      
      if (itemIndex !== -1) {
        const oldItem = currentData[itemIndex];
        const newItem = updater(oldItem);
        
        // Check if the item actually changed
        if (this.getItemHash(oldItem) !== this.getItemHash(newItem)) {
          currentData[itemIndex] = newItem;
          hasChanges = true;
          updatedCount++;
        }
      }
    }
    
    if (hasChanges) {
      this.data = currentData;
      this.saveToCache();
      this.notify();
    }
    
    return updatedCount;
  }

  getData(): T[] {
    // If not initialized, return current data (will be empty initially)
    return this.data;
  }

  subscribe(listener: Listener<T>) {
    this.listeners.push(listener);
    
    // Initialize if not already done, then call listener
    if (!this.initialized) {
      this.initialize().then(() => {
        listener(this.data);
      }).catch((error) => {
        console.warn(`Failed to initialize store for key "${this.key}":`, error);
        // Still call listener with current data even if initialization fails
        listener(this.data);
      });
    } else {
      // Already initialized, call listener immediately
      listener(this.data);
    }
    
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notify() {
    this.listeners.forEach(listener => listener(this.data));
  }

  // Fetch from remote (Supabase), update local store
  async syncFromRemote(fetcher: () => Promise<Remote[]>) {
    const remoteData = await fetcher();
    const data = this.adapter?.fromRemote ? this.adapter.fromRemote(remoteData) : (remoteData as unknown as T[]);
    this.setData(data);
  }

  // Push local data to remote (Supabase)
  async syncToRemote(pusher: (data: Remote[]) => Promise<void>) {
    const data = this.adapter?.toRemote ? this.adapter.toRemote(this.data) : (this.data as unknown as Remote[]);
    await pusher(data);
  }

  // Optional: clear cache
  async clearCache() {
    try {
      await del(this.key, this.store);
    } catch (error) {
      console.warn(`Failed to clear cache for key "${this.key}":`, error);
    }
    this.data = [];
    this.notify();
  }
}

// Central registry for all stores
class StoreRegistry {
  private stores: Record<string, GenericStore<unknown, unknown>> = {};

  getStore<T, Remote = T>(options: StoreOptions<T, Remote>): GenericStore<T, Remote> {
    if (!this.stores[options.key]) {
      this.stores[options.key] = new GenericStore<T, Remote>(options);
    }
    return this.stores[options.key] as GenericStore<T, Remote>;
  }

  clearAllCaches() {
    Object.values(this.stores).forEach(store => store.clearCache());
  }

  // Add method to clear all IndexedDB data
  async clearAllIndexedDB() {
    try {
      // This will force a new database to be created
      const dbName = 'generic-store-db-v2';
      const request = indexedDB.deleteDatabase(dbName);
      
      request.onsuccess = () => {
        console.log('IndexedDB database deleted successfully');
      };
      
      request.onerror = () => {
        console.error('Error deleting IndexedDB database');
      };
    } catch (error) {
      console.warn('Failed to clear IndexedDB:', error);
    }
  }
}

export const storeRegistry = new StoreRegistry();

// Utility function to clear all IndexedDB data and reset the cache
export async function clearAllIndexedDBCache() {
  try {
    // Clear all stores first
    storeRegistry.clearAllCaches();
    
    // Delete the entire IndexedDB database
    const dbName = 'generic-store-db-v2';
    const request = indexedDB.deleteDatabase(dbName);
    
    return new Promise<void>((resolve, reject) => {
      request.onsuccess = () => {
        console.log('IndexedDB database deleted successfully');
        // Reset initialization state
        resetIndexedDBInitialization();
        resolve();
      };
      
      request.onerror = () => {
        console.error('Error deleting IndexedDB database');
        reject(new Error('Failed to delete IndexedDB database'));
      };
    });
  } catch (error) {
    console.warn('Failed to clear IndexedDB:', error);
    throw error;
  }
}

// Utility function to check if IndexedDB is available
export function isIndexedDBSupported(): boolean {
  return typeof window !== 'undefined' && 'indexedDB' in window;
}

// Global initialization state
let isIndexedDBInitialized = false;
let initializationPromise: Promise<void> | null = null;

// Initialize IndexedDB globally
async function initializeIndexedDB(): Promise<void> {
  if (isIndexedDBInitialized) return;
  
  if (initializationPromise) {
    return initializationPromise;
  }
  
  initializationPromise = new Promise<void>((resolve) => {
    // Give IndexedDB time to initialize properly
    // This prevents concurrent access issues during startup
    setTimeout(() => {
      isIndexedDBInitialized = true;
      resolve();
    }, 100);
  });
  
  return initializationPromise;
}

// Reset global initialization state (useful for testing or recovery)
export function resetIndexedDBInitialization(): void {
  isIndexedDBInitialized = false;
  initializationPromise = null;
}

// Utility function to manually clear IndexedDB cache for a specific store
export async function clearStoreCache(storeKey: string): Promise<void> {
  try {
    const store = createStore('generic-store-db-v2', storeKey);
    await del(storeKey, store);
    console.log(`Cache cleared for store: ${storeKey}`);
  } catch (error) {
    console.warn(`Failed to clear cache for store "${storeKey}":`, error);
    throw error;
  }
}

// Utility function to get all store keys from IndexedDB
export async function getStoreKeys(): Promise<string[]> {
  try {
    const dbName = 'generic-store-db-v2';
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(dbName);
      
      request.onsuccess = () => {
        const db = request.result;
        const transaction = db.transaction(['keyval'], 'readonly');
        const store = transaction.objectStore('keyval');
        const keysRequest = store.getAllKeys();
        
        keysRequest.onsuccess = () => {
          resolve(keysRequest.result as string[]);
        };
        
        keysRequest.onerror = () => {
          reject(new Error('Failed to get store keys'));
        };
      };
      
      request.onerror = () => {
        reject(new Error('Failed to open database'));
      };
    });
  } catch (error) {
    console.warn('Failed to get store keys:', error);
    return [];
  }
}


